import { Observable, of } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LocalRefreshingCacheService {
  /** Default expiry in Milliseconds */
  defaultExpires = 86400000; // 24Hrs

  constructor(private localstorage: LocalStorageService) {}
  /**
   * Get the Cache value and return it as a observable combined with the passed in Observable,
   * Refreshing the cache with the newest value from the observable.
   * @param key: the key of the value to be refreshed
   * @param value$: A observable value to be cached
   * @param expiry: Optional time to expire default is 24 hours
   */
  public getCacheValueAndRefresh<T>(key: string, value$: Observable<T>, expiry?: number): Observable<T | undefined> {
    if (!key) {
      return of(undefined);
    }
    const cachedValue = this.getCacheValue(key);
    return value$.pipe(
      tap((newValue) => {
        this.setCacheValue(key, newValue, expiry);
        return newValue;
      }),
      startWith(cachedValue as T),
    );
  }

  /**
   * Update the Cached value for a key
   * @param key: the key to the value to be updated
   * @param newValue: a value to update the cache
   * @param expiry: (Optional) time to expire default is 24 hours
   */
  public setCacheValue<T>(key: string, newValue: T, expiry?: number): T | undefined {
    if (!key) {
      return;
    }
    return this.setCacheRecord(key, newValue, expiry);
  }

  /**
   * Remove the Cached entry for the key
   * @param key: the key to the value to be removed
   */
  public deleteCacheValue(key: string): void {
    if (key) {
      this.localstorage.remove(key);
    }
  }

  /**
   * Get value for the specified key from the local storage or undefined
   * @param key: the key or type to get for
   */
  public getCacheValue<T>(key: string): T | undefined {
    if (!key) {
      return undefined;
    }
    const cachedValue = this.getCacheRecord(key);
    if (cachedValue && cachedValue.value) {
      return cachedValue.value as T;
    }
    return undefined;
  }

  private getCacheRecord<T>(key: string): CacheStorageRecord<T> | null {
    let cachedValue = JSON.parse(this.localstorage.get(key)!) as CacheStorageRecord<T> | null;
    // If it exists check its expiry time
    if (cachedValue && cachedValue.expires) {
      // If the expiry is past, nullify the value
      if (new Date(cachedValue.expires).getTime() < Date.now()) {
        this.localstorage.remove(key);
        cachedValue = null;
      }
    }
    return cachedValue;
  }

  private setCacheRecord<T>(key: string, value: T, expires: number = this.defaultExpires): T {
    const _expires: Date = this.sanitizeAndGenerateDateExpiry(expires);
    this.localstorage.set(
      key,
      JSON.stringify({
        expires: _expires,
        value: value,
      }),
    );
    return value;
  }

  private sanitizeAndGenerateDateExpiry(expires: number): Date {
    const expiryDate: Date = new Date(Date.now() + Math.abs(expires));
    // Don't allow expiry dates in the past
    if (expiryDate.getTime() <= Date.now()) {
      return new Date(Date.now() + expires);
    }
    return expiryDate;
  }
}

export interface CacheStorageRecord<T> {
  expires: Date;
  value: T;
}
